import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Loader from '../common/Loader';
import SurveyList from '../Surveys/SurveyList';
import { CommonTabs } from '../common/CommonTabs';
import WithLazyLoad from '../../hocs/withLazyLoad';
import { SURVEYS_LIST_TYPE } from '../../constants';
import { isDesktop } from '../../utils/mobileHelpers';
import SurveysResults from '../Surveys/SurveysResults';
import EmptyContent from '../common/EmptyState/Content';
import { SURVEYS_ANSWERED_EMPTY } from '../../constants/strings';
import { enhance } from './enhance';
import MetaHelmet from '../AppHelmet/MetaHelmet';
import { usePageView } from '../../hocs/usePageView';

const DataSurvey = ({
  loadNextUpcomingReq,
  loadNextAnsweredReq,
  intl: { formatMessage },
  fetchAnsweredSurveys,
  fetchUpcomingSurveys,
  answeredListCounter,
  upcomingListCounter,
  answeredList,
  upcomingList,
  targetUserId,
  loading,
}) => {
  const [activeTab, setActiveTab] = useState(SURVEYS_LIST_TYPE.UPCOMING);
  const tabs = useTabs(upcomingListCounter, answeredListCounter);
  const isEmpty = !upcomingListCounter && !answeredListCounter;

  usePageView('Profile | Surveys');
  useEffect(() => {
    if (!targetUserId) return;
    fetchAnsweredSurveys({
      id: targetUserId,
      limit: 20,
    });

    fetchUpcomingSurveys({
      id: targetUserId,
      limit: 20,
    });
  }, [targetUserId]);

  useEffect(() => {
    if (!loading && !upcomingListCounter && answeredListCounter) {
      setActiveTab(SURVEYS_LIST_TYPE.RESULTS);
    }
  }, [upcomingListCounter, answeredListCounter, loading]);

  const loadMore = useCallback(() => {
    const params = {
      id: targetUserId,
      limit: 10,
    };
    if (activeTab === SURVEYS_LIST_TYPE.UPCOMING) loadNextUpcomingReq(params);
    if (activeTab === SURVEYS_LIST_TYPE.RESULTS) loadNextAnsweredReq(params);
  }, [activeTab]);

  const getCurrentSurveys = tab => {
    switch (tab) {
      case SURVEYS_LIST_TYPE.UPCOMING:
        return (
          <SurveyList
            surveys={upcomingList}
            loadMore={loadMore}
            className="pt3"
          />
        );
      case SURVEYS_LIST_TYPE.RESULTS:
        return (
          <WithLazyLoad loadMore={loadMore}>
            <SurveysResults
              surveys={answeredList}
              locked={false}
              hideTitle
              className="pt3"
            />
          </WithLazyLoad>
        );
      default:
        return null;
    }
  };

  return (
    <div className="h-100 w-90 relative center">
      <MetaHelmet title="pages.title.profile.surveys" />
      {loading && <Loader />}

      {!loading && !isEmpty && (
        <CommonTabs
          onTabSelect={setActiveTab}
          activeTab={activeTab}
          tabList={tabs}
          renderContent={tab => getCurrentSurveys(tab)}
          styleName="content"
        />
      )}

      {!loading && isEmpty && (
        <EmptyContent
          icon="no-surveys"
          text={formatMessage(SURVEYS_ANSWERED_EMPTY)}
          className="absolute-center g-color--primary"
        />
      )}
    </div>
  );
};

DataSurvey.propTypes = {
  loadNextUpcomingReq: PropTypes.func,
  loadNextAnsweredReq: PropTypes.func,
  intl: PropTypes.shape({
    formatMessage: PropTypes.func,
  }),
  fetchAnsweredSurveys: PropTypes.func,
  fetchUpcomingSurveys: PropTypes.func,
  answeredListCounter: PropTypes.number,
  upcomingListCounter: PropTypes.number,
  answeredList: PropTypes.array,
  upcomingList: PropTypes.array,
  targetUserId: PropTypes.number,
  loading: PropTypes.bool,
};

export default enhance(DataSurvey);

function useTabs(upcomingCounter, answeredCounter) {
  return useMemo(() => {
    const tabs = [];
    if (upcomingCounter)
      tabs.push({
        label: 'surveys.actions.upcomingTitle',
        tab: SURVEYS_LIST_TYPE.UPCOMING,
        id: SURVEYS_LIST_TYPE.UPCOMING,
        counter: upcomingCounter,
      });

    if (answeredCounter)
      tabs.push({
        label: 'surveys.actions.resultsTitle',
        tab: SURVEYS_LIST_TYPE.RESULTS,
        id: SURVEYS_LIST_TYPE.RESULTS,
        counter: answeredCounter,
      });
    return tabs;
  }, [upcomingCounter, answeredCounter]);
}



// WEBPACK FOOTER //
// ./src/components/DataSurveys/index.jsx